import { observer } from "mobx-react-lite";
import React, { Fragment, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import AsyncSelect from 'react-select/async';
import { DropdownItem, DropdownMenu, DropdownToggle, InputGroup, Dropdown } from "reactstrap";
import { Account } from "../../data/fetch";
import { RootStoreContext } from "../../store/rootStore";
import { SearchBy, SearchItem } from "../../store/searchStore";
import { splitLocaleQueryParams } from "../../utils";

interface IProps {
	location: any;
}

const HeaderSearch = observer((props: IProps) => {
	const rootStore = useContext(RootStoreContext);
	const navigate = useNavigate();
	const {recentSearch, updateRecentSearchList, findSearchByQuery, clearRecentList } = rootStore.searchStore;
	const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
	const [searchBy, setSearchBy] = useState<SearchBy>(SearchBy.Text);
	const [searchItem, setSearchItem] = useState<SearchItem | null>(() => {
		const values = props.location ? splitLocaleQueryParams(props.location.search, true) : null;
		return findSearchByQuery(values);
	});

	const onMenuClick = (s: SearchItem) => {
		if (s.searchBy === undefined)
			s.searchBy = SearchBy.Text;
		if (s.searchBy !== searchBy)
			setSearchBy(s.searchBy);
		setSearchItem(s);
		updateRecentSearchList(s);
		navigate(`/`);
	}

	const doSearch = (s: SearchItem | null) => {
		if (s ) {
			if( s.searchBy === undefined)
				s.searchBy = searchBy;
		}

		updateRecentSearchList(s);
		navigate(`/`);
	}

	const onSearchModeChanged = (s: SearchBy) => {
		setSearchBy(s);
		setSearchItem(null);
	}

	const onChange = (e: any, a: any) => {
		const action = a ? a.action : '';
		//debugLog(`onChange label:'${e ? e.label : '???'}' value:'${e ? e.value : '???'}' action:'${action}`);
		if (!e) {
			if (action === 'clear')
				onClear();
		}
		else if (e.label && e.value) {
			setSearchItem({ searchBy: searchBy, label: e.label, value: e.value });
			doSearch(e);
		}
	};

	const onInputChange = (e: any, a: any) => {
		//debugLog(`onInputChange value:'${e}' action:'${a.action}`);
		if( a.action==='input-change')
			setSearchItem({ searchBy: searchBy, label: e, value: e });
		else if (a.action === 'input-blur')
			setSearchItem(null);
	};

	const onClear = () => {
		//debugLog('onClear:');
		if (searchItem)
			setSearchItem(null);
		doSearch(null);
	};

	const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		//traceRendering('Form submitted..');
		e.preventDefault();
		doSearch(searchItem);
	};

	const textRecentSearch = recentSearch.filter(s => s.searchBy == SearchBy.Text);

	const loadText = async (inputValue: any, action: any) => {
		//debugLog(`loadText: '${inputValue}' action: ${action.name}`);
		const arr = recentSearch.filter(s => {
			if (s.searchBy != SearchBy.Text)
				return false;
			return !inputValue || (s.value && s.value.toString().indexOf(inputValue) >= 0);
		})
		return arr;
	}

	const loadAccount = async (inputValue: any, action: any) => {
		//debugLog(`loadAccount: '${inputValue}' action: ${action.name}`);
		return await Account.getList(inputValue);
	}

	const optionSettings = () =>
		searchBy === SearchBy.Account ? {
			placeholder: 'Select account and hit enter...',
			loadOptions: loadAccount,
			noOptionsMessage: () => 'No account found'
		} : {
			placeholder: 'Type text to search and hit enter...',
			loadOptions: loadText,
			defaultOptions: textRecentSearch,
			noOptionsMessage: () => 'No recent search found'
		}

	const renderRecentList = () => {
		if (!recentSearch || !recentSearch.length)
			return null;
		return (
			<Fragment>
				<DropdownItem key='clearCache' onClick={() => clearRecentList()}>Clear Recent List</DropdownItem>
				<DropdownItem divider />
				{ recentSearch.map((item, i) => (<DropdownItem key={'h-' + i} onClick={() => onMenuClick(item)}>{item.label}</DropdownItem>)) }
			</Fragment>
		)
	}	

	//debugLog('Search render: ' + search ? JSON.stringify(search) : 'null' );
	return (
		<form
			className="navbar-form"
			role="search"
			onSubmit={onSubmit}
		>
			<InputGroup className="form-group">
				<AsyncSelect
					{...optionSettings()}
					closeMenuOnSelect
					onChange={onChange}
					onInputChange={onInputChange}
					key='searchBySelector'
					className="form-control"
					name={'label'}
					value={searchItem && searchItem.label ? searchItem : undefined}
					styles={selectorStyles}
					cacheOptions={false}
					isClearable
				/>
				<Dropdown 
					addonType="append"
					isOpen={dropdownOpen}
					toggle={() => setDropdownOpen(!dropdownOpen)}>
					<DropdownToggle outline style={{ margin: 0, padding: 5 }}>
						<div className={searchBy === SearchBy.Text ? "search-icon" : "search-account-icon"} />
					</DropdownToggle>
					<DropdownMenu name='searchBy'>
						<DropdownItem key='byText' onClick={() => onSearchModeChanged(SearchBy.Text)}>Search by Text</DropdownItem>
						<DropdownItem key='byAccount' onClick={() => onSearchModeChanged(SearchBy.Account)}>Search by Account</DropdownItem>
						{renderRecentList()}
					</DropdownMenu>
				</Dropdown>
			</InputGroup>
			<button className="d-none" type="submit">Submit</button>
		</form>
	);
});

export default HeaderSearch;

const selectorStyles = {
	control: (base: any) => {
		const style = { ...base };
		style.boxShadow = undefined;
		style.borderRadius = undefined;//0;
		style.borderWidth = undefined;//'0px';
		style.borderStyle = undefined;//'none';
		style.width = '100%';// 320;
		style.height = '100%';
		style.minHeight = undefined;
		return style;
	},
	input: (base: any) => {
		const style = { ...base };
		style.margin = 0;
		style.paddingTop = style.paddingBottom = 0;
		style.height = '100%';
		style.minHeight = undefined;
		return style;
	},
	placeholder: (base: any) => {
		const style = { ...base };
		style.margin = 0;
		style.paddingTop = style.paddingBottom = 0;
		style.height = '100%';
		style.minHeight = undefined;
		return style;
	},
	valueContainer: (base: any) => {
		const style = { ...base };
		style.width = '100%';// 320;
		style.margin = 0;
		style.padding = 0;
		style.boxShadow = undefined;
		style.borderRadius = undefined;//0;
		style.borderWidth = undefined;//'0px';
		style.borderStyle = undefined;//'none';
		style.minHeight = undefined;
		return style;
	},

	indicatorSeparator: (base: any) => ({
		display: 'none',
	}),
	dropdownIndicator: (base: any) => ({
		display: 'none',
	}),
	clearIndicator: (base: any) => ({
		...base,
		padding: 0,
		minHeight: undefined
	})
};